import React, { useState } from 'react';
import { FileText, Shield, Mail, ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';

const Footer = () => {
  const [aszfOpen, setAszfOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  return (
    <footer className="bg-gray-950 text-white">

      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12">

          {/* Brand column */}
          <div className="md:col-span-2 space-y-5">
            <div className="flex items-center gap-2.5">
              <span className="text-lg font-bold text-white">
                MitVegyek<span className="text-blue-400">AI</span>
              </span>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed max-w-sm">
              AI-alapú termékajánló motor webshopoknak. Hibrid keresés és LLM rangsorolás — pontosan azt adja a vevőidnek, amit keresnek.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-1">
              <a
                href="#kapcsolat"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-full transition-colors"
              >
                Kapcsolatfelvétel
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
              <a
                href="mailto:info@mitvegyek.hu"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/15 text-white text-sm font-medium rounded-full transition-colors"
              >
                <Mail className="w-3.5 h-3.5" />
                info@mitvegyek.hu
              </a>
            </div>
          </div>

          {/* Termék column */}
          <div className="space-y-4">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Termék</p>
            <ul className="space-y-2.5">
              {[
                { label: 'Funkciók', href: '#funkciok' },
                { label: 'Widget Demó', href: '#widget-demo' },
                { label: 'Analitika', href: '#analitika' },
              ].map(link => (
                <li key={link.label}>
                  <a href={link.href} className="text-sm text-gray-400 hover:text-white transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Highlights column */}
          <div className="space-y-4">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Miért válassz minket</p>
            <ul className="space-y-3">
              {[
                { label: '50ms válaszidő', sub: 'Azonnali eredmények' },
                { label: 'GDPR kompatibilis', sub: 'EU adatvédelem' },
                { label: 'Plug & Play', sub: '1 script, 5 perc integráció' },
              ].map(item => (
                <li key={item.label} className="flex gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-white leading-tight">{item.label}</p>
                    <p className="text-xs text-gray-500">{item.sub}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-600">
              © 2026 MitVegyek AI. Minden jog fenntartva.
            </p>
            <div className="flex items-center gap-5">
              <button
                id="footer-aszf-btn"
                onClick={() => setAszfOpen(true)}
                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                <FileText className="w-3.5 h-3.5" />
                ÁSZF
              </button>
              <button
                id="footer-privacy-btn"
                onClick={() => setPrivacyOpen(true)}
                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                <Shield className="w-3.5 h-3.5" />
                Adatkezelés
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ÁSZF Modal */}
      <Dialog open={aszfOpen} onOpenChange={setAszfOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100">
            <DialogTitle className="text-xl font-bold text-gray-900">
              Általános Szerződési Feltételek (ÁSZF)
            </DialogTitle>
            <p className="text-sm text-gray-500 mt-1">MitVegyekAI</p>
          </DialogHeader>
          <ScrollArea className="px-6 py-5 max-h-[60vh]">
            <div className="space-y-6 text-sm text-gray-700 leading-relaxed pr-4">
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-2">1. Általános rendelkezések</h3>
                <p>A jelen ÁSZF szabályozza a MitVegyekAI (továbbiakban: Szolgáltató) által nyújtott API-alapú termékajánló szoftver (SaaS) igénybevételének feltételeit. A szolgáltatás használatával a Partner elfogadja a jelen feltételeket.</p>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-2">2. A Szolgáltatás tárgya és licence</h3>
                <div className="space-y-3">
                  <p><span className="font-semibold text-gray-800">Licenc:</span> A Szolgáltató nem kizárólagos, nem átruházható használati jogot biztosít a Partnernek a MitVegyekAI API-hoz és a Widgethez.</p>
                  <p><span className="font-semibold text-gray-800">Fejlesztés:</span> A Szolgáltató fenntartja a jogot a szoftver folyamatos frissítésére és a modellek (LLM, embedding) módosítására a jobb relevancia érdekében.</p>
                </div>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-2">3. Rendelkezésre állás és SLA</h3>
                <ul className="space-y-2">
                  {['A Szolgáltató törekszik az évi 99.5%-os rendelkezésre állásra.', 'A tervezett karbantartásokról a Szolgáltató legalább 24 órával korábban értesíti a Partnert.', 'Nem minősül üzemzavarnak a harmadik féltől származó szolgáltatások (pl. OpenAI API, AWS/Azure) kimaradása.'].map(t => (
                    <li key={t} className="flex gap-2"><span className="text-blue-600 font-semibold">•</span><span>{t}</span></li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-2">4. Felelősségkorlátozás</h3>
                <div className="space-y-3">
                  <p><span className="font-semibold text-gray-800">AI-tartalom:</span> Mivel a termékindoklásokat AI generálja, a Szolgáltató nem felel a szövegek nyelvi pontosságáért vagy ténybeli tévedéseiért.</p>
                  <p><span className="font-semibold text-gray-800">Anyagi felelősség:</span> A kártérítés maximális mértéke nem haladhatja meg a Partner által az utolsó 3 hónapban kifizetett szolgáltatási díjat.</p>
                </div>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-2">5. Díjazás és fizetési feltételek</h3>
                <ul className="space-y-2">
                  {['A szolgáltatás havidíjas vagy forgalmi alapú (kérésszám).', 'A számlázás havonta utólag vagy előre történik.', '8 napot meghaladó fizetési késedelem esetén a Szolgáltató jogosult az API-kulcs ideiglenes felfüggesztésére.'].map(t => (
                    <li key={t} className="flex gap-2"><span className="text-blue-600 font-semibold">•</span><span>{t}</span></li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-2">6. Időtartam és felmondás</h3>
                <p>A szerződés határozatlan időre köteleződik. Bármelyik fél jogosult 30 napos felmondási idővel, indoklás nélkül felmondani a szolgáltatást.</p>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Adatkezelési Modal */}
      <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100">
            <DialogTitle className="text-xl font-bold text-gray-900">
              Adatvédelmi és Adatkezelési Tájékoztató
            </DialogTitle>
            <p className="text-sm text-gray-500 mt-1">MitVegyekAI</p>
          </DialogHeader>
          <ScrollArea className="px-6 py-5 max-h-[60vh]">
            <div className="space-y-6 text-sm text-gray-700 leading-relaxed pr-4">
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-2">1. Bevezetés</h3>
                <p>A MitVegyekAI elkötelezett a GDPR (EU 2016/679) szerinti adatvédelem mellett. Mivel mi csak a webshop látogatóinak szándékát (keresési szavak, preferenciák) elemezzük, és nem kérünk tőlük nevet vagy e-mailt, a rendszerünk alapértelmezetten Privacy by Design elvű.</p>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-2">2. Adatfeldolgozói minőség</h3>
                <p>A Partner az Adatkezelő, a MitVegyekAI az Adatfeldolgozó. Ez azt jelenti, hogy mi csak a Partner utasítására (az API hívásakor) dolgozzuk fel a látogatói adatokat.</p>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-2">3. Kezelt adatok köre és célja</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden mt-3">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left px-3 py-2.5 font-semibold text-gray-800 border-b border-gray-200">Adat típusa</th>
                        <th className="text-left px-3 py-2.5 font-semibold text-gray-800 border-b border-gray-200">Cél</th>
                        <th className="text-left px-3 py-2.5 font-semibold text-gray-800 border-b border-gray-200">Jogalap</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['Keresési kifejezés', 'Releváns ajánlás generálása', 'Szerződés teljesítése'],
                        ['IP-cím', 'Rate limit, biztonság', 'Jogos érdek'],
                        ['Referrer URL', 'API hozzáférés ellenőrzése', 'Szerződés teljesítése'],
                        ['Kattintási adatok', 'Konverziós statisztika', 'Jogos érdek'],
                      ].map(([a, b, c]) => (
                        <tr key={a} className="border-b border-gray-100 last:border-0">
                          <td className="px-3 py-2.5 text-gray-700">{a}</td>
                          <td className="px-3 py-2.5 text-gray-600">{b}</td>
                          <td className="px-3 py-2.5 text-gray-600">{c}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-2">4. Adattovábbítás (OpenAI és tárhely)</h3>
                <p>A Partner tudomásul veszi, hogy a szöveges bemenetek feldolgozása során az adatok (személyes azonosítók nélkül) továbbításra kerülnek az OpenAI (USA) szervereire. Az OpenAI-val kötött szerződésünk garantálja, hogy az adatokat nem használják fel modelljeik tanítására.</p>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-2">5. Az érintettek jogai</h3>
                <p>Bár a MitVegyekAI közvetlenül nem tárol azonosítható személyes adatot, a webshop látogatói a Partneren keresztül kérhetik adataik törlését vagy helyesbítését, aminek a MitVegyekAI 72 órán belül eleget tesz.</p>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </footer>
  );
};

export default Footer;
